import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef } from 'react';
import { fetchPolygonSnapshotPriceses } from '../composables/fetchPolygon';
import { isCryptoSymbol, applyRealtimeCryptoQuote } from '../composables/fetchCrypto';
import { realtimeSocket } from '../composables/realtimeSocket';

interface MarketPriceContextType {
  currentPrices: { [symbol: string]: number };
  fetchPricesForSymbols: (symbols: string[]) => Promise<void>;
  isLoadingPrices: boolean;
  isRealtimeConnected: boolean;
}

// Crypto ผ่าน WebSocket ส่งแท่ง XA ราวนาทีละครั้งต่อเหรียญ ถ้าเงียบเกินนี้ถือว่า WS ไม่ส่งแล้ว ให้กลับไป polling
const REALTIME_FRESH_MS = 150000;
// รวมราคาจาก WS แล้วค่อย setState ทีเดียว กันไม่ให้ทั้งแอป re-render ทุกข้อความ
const REALTIME_FLUSH_MS = 1000;

const MarketPriceContext = createContext<MarketPriceContextType | undefined>(undefined);

export const MarketPriceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentPrices, setCurrentPrices] = useState<{ [symbol: string]: number }>({});
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);

  // 🌟 เก็บรายชื่อหุ้นทั้งหมดที่มีการเรียกใช้งาน เพื่อให้ตัว Interval วนลูปอัปเดตถูกตัว
  const trackedSymbolsRef = useRef<Set<string>>(new Set());
  // เวลาที่ได้รับราคาจาก WebSocket ล่าสุดของแต่ละ symbol (เวลาเครื่องเรา)
  const realtimeReceivedAtRef = useRef<Map<string, number>>(new Map());
  const pendingRealtimeRef = useRef<{ [symbol: string]: number }>({});

  // ฟังก์ชันดึงราคา (รองรับทั้งแบบเช็ค missing และแบบบังคับอัปเดตสด)
  const fetchPrices = async (symbols: string[], force = false) => {
    if (!symbols || symbols.length === 0) return;

    // ถ้าไม่ใช่โหมดบังคับ ให้กรองเฉพาะตัวที่ยังไม่มีในถัง
    const targetSymbols = force
      ? symbols
      : symbols.filter(sym => !currentPrices[sym]);

    if (targetSymbols.length === 0 && !force) return;

    setIsLoadingPrices(true);
    try {
      const newPrices = await fetchPolygonSnapshotPriceses(targetSymbols);

      setCurrentPrices(prev => ({
        ...prev,
        ...newPrices
      }));
    } catch (error) {
      console.error("Failed to fetch market prices:", error);
    } finally {
      setIsLoadingPrices(false);
    }
  };

  const fetchPricesForSymbols = useCallback(async (symbols: string[]) => {
    // บันทึกลง Ref ไว้ให้ตัว Interval รู้ว่าต้องคอยส่องหุ้นตัวไหนบ้าง
    let hasNewCrypto = false;
    symbols.forEach(sym => {
      if (!trackedSymbolsRef.current.has(sym) && isCryptoSymbol(sym)) hasNewCrypto = true;
      trackedSymbolsRef.current.add(sym);
    });

    // Crypto ใช้ WebSocket (หุ้นยัง polling เหมือนเดิม)
    if (hasNewCrypto) {
      realtimeSocket.setSymbols(Array.from(trackedSymbolsRef.current).filter(isCryptoSymbol));
    }

    // ดึงรอบแรกผ่าน REST เสมอ จะได้มีราคาโชว์ทันทีไม่ต้องรอ WS
    await fetchPrices(symbols, false);
  }, [currentPrices]);

  // 🌟 รับราคาจาก WebSocket แล้วทยอย flush ลง state
  useEffect(() => {
    const offQuotes = realtimeSocket.onQuotes(quotes => {
      const now = Date.now();
      quotes.forEach(q => {
        if (!isCryptoSymbol(q.symbol)) return;
        applyRealtimeCryptoQuote(q);
        realtimeReceivedAtRef.current.set(q.symbol, now);
        if (q.price > 0) pendingRealtimeRef.current[q.symbol] = q.price;
      });
    });
    const offStatus = realtimeSocket.onStatus(setIsRealtimeConnected);

    const flush = setInterval(() => {
      const pending = pendingRealtimeRef.current;
      if (Object.keys(pending).length === 0) return;
      pendingRealtimeRef.current = {};
      setCurrentPrices(prev => ({ ...prev, ...pending }));
    }, REALTIME_FLUSH_MS);

    return () => {
      offQuotes();
      offStatus();
      clearInterval(flush);
    };
  }, []);

  // 🌟 🌟 🌟 ส่วนสำคัญ: กลไก Interval กลาง ทุกๆ 14 วินาที 🌟 🌟 🌟
  useEffect(() => {
    const interval = setInterval(async () => {
      const now = Date.now();
      // ข้าม crypto ที่ WS ยังส่งราคามาอยู่ ตัวที่เงียบ/ต่อ WS ไม่ได้ ให้ดึงแบบเดิมเป็นสำรอง
      const symbolsToUpdate = Array.from(trackedSymbolsRef.current).filter(sym => {
        if (!isCryptoSymbol(sym) || !realtimeSocket.isConnected()) return true;
        const receivedAt = realtimeReceivedAtRef.current.get(sym);
        return !receivedAt || now - receivedAt > REALTIME_FRESH_MS;
      });
      if (symbolsToUpdate.length > 0) {
        // สั่งดึงราคาแบบ force = true เพื่อบังคับอัปเดตสดใหม่จาก Gateway ทุก 14 วิ
        await fetchPrices(symbolsToUpdate, true);
      }
    }, 14000); // ทุกๆ 14 วินาที

    return () => clearInterval(interval);
  }, []);

  return (
    <MarketPriceContext.Provider value={{ currentPrices, fetchPricesForSymbols, isLoadingPrices, isRealtimeConnected }}>
      {children}
    </MarketPriceContext.Provider>
  );
};

export const useMarketPrices = () => {
  const context = useContext(MarketPriceContext);
  if (!context) {
    throw new Error('useMarketPrices must be used within a MarketPriceProvider');
  }
  return context;
};
